import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import useLadyService from '@/services/LadyService';
import {
	getAgencyID,
	getCountriesData,
	getUserCurrentCity,
	getUserCurrentDistrict,
	setCity,
	setCurrentCity,
	setUserCurrentDistrict,
} from '@/stores/slices/userSlice';
import {
	getAllModels,
	getIsMainPage,
	getPage,
	getSearchValue,
	setAllModels,
	setHasMorePage,
	setIsLoading,
} from '@/stores/slices/modelsState';
import {
	getFilterQuery,
	getFilterState,
	getSortParam,
	resetUnchangedRanges,
	setDefaultRangeState,
	setDisabledFilters,
	setProfilesCount,
	setProfilesOnMapCount,
	updateFilter,
} from '@/stores/slices/filterSlice';
import { useLocation, useParams } from 'react-router-dom';
import baseUrl from '@/services/apiConfig';
import showToast from '@/components/toast/Toast';

export const useProfilesFetching = () => {
	const { lang, token, t } = useLadyService();
	const params = useParams();

	const { uuid } = useParams();

	const dispatch = useDispatch();
	const currentCity = useSelector(getUserCurrentCity);
	const currentDistrict = useSelector(getUserCurrentDistrict);
	const agencyId = useSelector(getAgencyID);
	const page = useSelector(getPage);
	const filterQuery = useSelector(getFilterQuery);
	const location = useLocation();
	const sort_param = useSelector(getSortParam);
	const searchValue = useSelector(getSearchValue);
	const allModels = useSelector(getAllModels);
	const isMainPage = useSelector(getIsMainPage);
	const filterState = useSelector(getFilterState);

	// Memoize city/district slugs to avoid unnecessary dependency changes
	const citySlug = useMemo(() => currentCity?.slug, [currentCity?.slug]);
	const districtId = useMemo(
		() => currentDistrict?.id,
		[currentDistrict?.id],
	);

	const parseDisabledFilters = (filters) => {
		const disabledFilters = [];
		const default_ranges = {
			age: { min: 0, max: 0 },
			price: { min: 0, max: 0 },
			height: { min: 0, max: 0 },
			weight: { min: 0, max: 0 },
			penis: { min: 0, max: 0 },
		};
		// Итерация по всем свойствам объекта
		Object.entries(filters).forEach(([key, value]) => {
			if (key.includes('_min')) {
				default_ranges[key.replace('_min', '')].min = value;
			}

			if (key.includes('_max')) {
				default_ranges[key.replace('_max', '')].max = value;
			}

			// Пропускаем вложенные объекты
			if (typeof value === 'object' && value !== null) {
				return;
			}

			// Если значение false — добавляем ключ
			if (value === false) {
				disabledFilters.push(key);
			}
		});

		const filterMapping = {
			// status
			statusHealthControl: 'statusHealthControl',
			statusIndividual: 'statusIndividual',

			// ethnos
			ethnosArabic: 'ethnosArabic',
			ethnosIndian: 'ethnosIndian',
			ethnosMulatto: 'ethnosMulatto',

			// penis
			penisSmall: 'penisSmall',

			// sex
			sexHomosexual: 'sexHomosexual',
			sexLesbian: 'sexLesbian',

			// special
			specialLipsPumped: 'specialLipsPumped',
			hairIntimHaircut: 'hairIntimHaircut',

			// place/status
			placeIncall: 'placeIncall',
			placeOutcall: 'placeOutcall',
			placeEscort: 'placeEscort',
			statusMassage: 'statusMassage', // для eromassage-1

			// gender (если нужно)
			genderFemale: 'genderFemale',
			genderMale: 'genderMale',
			genderTrans: 'genderTrans',
			genderCouple: 'genderCouple',
		};

		const syncedDisabledFilters = disabledFilters.filter(
			(key) => filterMapping[key],
		);

		return { disabledFilters, syncedDisabledFilters, default_ranges };
	};

	const [showSkeletons, setShowSkeletons] = useState(true);
	const [isDataEmpty, setIsDataEmpty] = useState(false);
	const [showMore, setShowMore] = useState(false);
	const requestIdRef = useRef(0);
	const timeoutIdRef = useRef(null);
	const controllerRef = useRef(null);
	const isFetchingRef = useRef(false);

	const countriesData = useSelector(getCountriesData);
	// const lastFilterQueryRef = useRef('empty');

	// Основная функция fetch
	const fetchProfiles = useCallback(
		async (isLoadMore = false) => {
			// Защита от множественных одновременных запросов
			// if (isFetchingRef.current) {
			// 	console.info(
			// 		`%cПропускаем - уже выполняется запрос`,

			// 		`color:gray; font-size: 10px;`,
			// 	);

			// 	return;
			// }

			const currentRequestId = ++requestIdRef.current;
			// console.info(
			// `%cНачинаем запрос #${currentRequestId}`,
			// `color:rgb(209, 154, 2); font-size: 10px;`,
			// );

			// Очистка предыдущих запросов
			if (timeoutIdRef.current) {
				console.info(
					`%cTIMEOUT CLEANUP предыдущего запроса`,
					`color:rgb(156, 49, 49); font-size: 10px;`,
				);
				clearTimeout(timeoutIdRef.current);
				timeoutIdRef.current = null;
			}

			if (controllerRef.current) {
				console.info(
					`%cABORT CLEANUP предыдущего запроса`,
					`color:rgb(156, 49, 49); font-size: 10px;`,
				);
				controllerRef.current.abort();
				controllerRef.current = null;
			}

			// Показываем скелетоны если не подгрузка
			if (!isLoadMore) {
				setShowSkeletons(true);
				dispatch(setAllModels([]));
			}

			// Устанавливаем флаг выполнения
			isFetchingRef.current = true;

			// Создаем новый таймаут
			timeoutIdRef.current = setTimeout(async () => {
				try {
					// Проверяем актуальность запроса
					// if (currentRequestId !== requestIdRef.current) {
					// 	console.info(
					// 		`%cЗапрос #${currentRequestId} устарел`,
					// 		`color:rgb(156, 49, 49); font-size: 10px;`,
					// 	);
					// 	return;
					// }

					// Создаем контроллер для отмены
					controllerRef.current = new AbortController();

					// Строим URL
					const agneciesLocation =
						location.pathname.includes('/agency/');

					const agencyFilter =
						(params.agencyId || agencyId) &&
						agneciesLocation &&
						!isMainPage
							? `&agency_id=${params.agencyId ?? agencyId}`
							: '';

					// console.log(
					// 	'=> checkForAgency: ',
					// 	'agencyFilter: ',
					// 	agencyFilter,
					// 	'agencyId: ',
					// 	agencyId,
					// 	'isMainPage: ',
					// 	isMainPage,
					// 	'location.includes: ',
					// 	agneciesLocation,
					// );

					const cityFilter = citySlug ? `&region=${citySlug}` : '';

					const districtFilter =
						districtId && cityFilter
							? `&district=${districtId}`
							: '';

					// filterQuery is already built by buildFilterParams
					// Just join it with other filters
					// console.log(`=> FILTER FETCH QUERY: `, filterQuery);

					const unchangedRanges = Object.keys(
						filterState.ranges?.current_state ?? {},
					).filter((rangeKey) => {
						const current =
							filterState.ranges?.current_state?.[rangeKey];
						const defaultRange =
							filterState.ranges?.default_state?.[rangeKey];

						return (
							JSON.stringify(current) ===
							JSON.stringify(defaultRange)
						);
					});

					let query = filterQuery.length
						? '&' + filterQuery.join('&')
						: '';

					if (uuid) {
						try {
							const res = await axios.request({
								headers: {
									Authorization: token
										? `Bearer ${token}`
										: '',
								},
								method: 'GET',
								url: `https://api.lady4love.com/api/filter/${uuid}?lang=${lang}}`,
							});
							if (res) {
								const saved_filter = res.data.data;
								console.log('=> saved_filter', saved_filter);
								const saved_city_slug =
									saved_filter?.filterState?.city || saved_filter?.filterState?.sity_slug;
								const saved_dist_slug =
									saved_filter?.filterState?.dist_slug;

								console.log(
									`=> saved_city_slug: `,
									saved_city_slug,
								);

								if (
									saved_city_slug &&
									saved_city_slug !== 'czechia'
								) {
									dispatch(setCity(saved_city_slug));

									const country = countriesData[0]['cz'];
									console.log(`=> country: `, country);
									const i = country.cities.find(
										(i) => i.slug === saved_city_slug,
									);
									console.log(`=> i: `, i);

									dispatch(
										setCurrentCity({
											id: i.id,
											title: i.name,
											icon: 'CZ',
											highlight: i.highlight,
											slug: i.slug,
											districts: i.districts,
										}),
									);

									if (saved_dist_slug) {
										const district = i.districts.find(
											(item) =>
												item?.slug === saved_dist_slug,
										);
										if (district) {
											dispatch(
												setUserCurrentDistrict(
													district,
												),
											);
										} else {
											dispatch(
												setUserCurrentDistrict({}),
											);
										}
									}
								}

								query =
									saved_filter.filterState.filter_query.join(
										'&',
									);
								// console.log('=> got filter', query);
								dispatch(
									updateFilter(saved_filter.filterState),
								);
							}
						} catch (e) {
							console.error(e);
							showToast({
								message: t('oops'),
								subMessage: `Error fetching filter by uuid, Server error ${e?.responce?.status}`,
								variant: `error`,
							});
						} finally {
							setShowSkeletons(false);
							setShowMore(false);
						}
					}

					let url = `${baseUrl}profiles?page=${page}&per_page=24&sort=${sort_param}${searchValue ? `&search=${searchValue}` : ''}${query ? `&${query}` : ''}${agencyFilter !== '' ? '&region=czechia' : cityFilter}&lang=${lang}${agencyFilter}${districtFilter}`;

					// console.log(`=> FILTER FETCH FINAL PATH: `, url);

					// console.info(
					// `%cВыполняем запрос #${currentRequestId}: ${url}`,
					// `color:lightblue; font-size: 10px;`,
					// );

					// if (page === 1) {
					// 	window.scrollTo(0, 0);
					// }

					// Выполняем запрос
					const response = await axios.request({
						signal: controllerRef.current.signal,
						headers: {
							Authorization: token ? `Bearer ${token}` : '',
						},
						method: 'GET',
						url,
					});

					// Проверяем актуальность ответа
					if (currentRequestId !== requestIdRef.current) {
						// console.info(
						// `%cОтвет #${currentRequestId} устарел`,
						// `color:rgb(173, 34, 34); font-size: 10px;`,
						// );
						return;
					}

					// console.info(
					// `%cОбрабатываем ответ #${currentRequestId}`,
					// `color:#1ca182; font-size: 10px;`,
					// );

					// Обрабатываем данные
					const data = response.data.profiles;
					const meta = response.data.meta;
					const isLoadMoreAvailable =
						meta.per_page * meta.current_page < meta.total;
					const parse_filters = parseDisabledFilters(
						response.data.filters,
					);

					// console.info(
					// 	'DATA RECEIVED:',
					// 	data.length,
					// 	'profiles;\n FULL RESULT:',
					// 	response.data,
					// );

					// Обновляем состояние

					dispatch(setDisabledFilters(parse_filters.disabledFilters));
					if (page === 1) {
						dispatch(setAllModels(data));
					} else {
						dispatch(setAllModels([...allModels, ...data]));
					}
					dispatch(setHasMorePage(isLoadMoreAvailable));
					dispatch(setIsLoading(false));
					dispatch(setProfilesCount(meta.total));
					dispatch(setProfilesOnMapCount(meta.count_on_map));

					if (unchangedRanges.length > 0) {
						// console.log(
						// 	'=> Detected unchanged ranges: ',
						// 	unchangedRanges,
						// 	'\nreseting...',
						// );

						dispatch(
							resetUnchangedRanges({
								unchangedRanges,
								default_state: parse_filters.default_ranges,
							}),
						);
					}

					dispatch(
						setDefaultRangeState(parse_filters.default_ranges),
					);

					setShowSkeletons(false);
					setShowMore(false);

					// console.log(
					// 	'=> received total',
					// 	meta.total,
					// 	'on map: ',
					// 	meta.count_on_map,
					// );

					setIsDataEmpty(data.length === 0);
				} catch (error) {
					// Игнорируем ошибки отмены
					if (
						axios.isCancel(error) ||
						error.code === 'ERR_CANCELED' ||
						error.name === 'AbortError'
					) {
						console.info(
							`%cЗапрос #${currentRequestId} отменен`,
							`color:rgb(156, 49, 49); font-size: 10px;`,
						);
						return;
					}

					// Обрабатываем реальные ошибки
					console.error('FETCH ERROR: ', error);
					dispatch(setIsLoading(false));

					const res = error.response;
					showToast({
						message: t('oops'),
						subMessage: `Server error ${res?.status}`,
						variant: `error`,
					});
				} finally {
					// Сбрасываем флаги
					isFetchingRef.current = false;
					timeoutIdRef.current = null;
					controllerRef.current = null;
				}
			}, 500); // Debounce 500ms
		},
		[
			// filterQuery, page, and location primitives only
			// NOT filterState - so modal changes don't trigger fetches
			sort_param,
			searchValue,
			page,
			citySlug,
			districtId,
			agencyId,
			filterQuery, // filterQuery from Redux, NOT filterState
			token,
			lang,
			searchValue,
			dispatch,
			t,
		],
	);

	const fetchProfilesCount = useCallback(
		(result, city = false) => {
			// here to check districts handling
			const resultUrl = `${baseUrl}profiles/count?lang=${lang}&region=${
				city ? city : currentCity.slug
			}${currentDistrict?.id ? `&district=${currentDistrict?.id}` : ``}`;
			const params = result.length ? `&${result.join('&')}` : '';

			axios
				.request({
					headers: {
						Authorization: token ? `Bearer ${token}` : '',
					},
					method: 'GET',
					url: resultUrl + params,
				})
				.then((response) => {
					dispatch(setProfilesCount(response.data.count));
					return response.data.count;
				})
				.catch((error) => {
					console.error(error);
					const res = error.responce;

					showToast({
						message: t('oops'),
						subMessage: `server error ${res?.status}`,
						variant: `error`,
					});

					return 0;
				});
		},
		[lang, currentCity, currentDistrict],
	);

	const filter_query_string = useMemo(() => {
		try {
			return filterQuery.join('&');
		} catch (e) {
			console.error('=> filterQuery error', filterQuery);
			return filterQuery ?? '_';
		}
	}, [filterQuery]); // Direct dependency on filterQuery, not filterState

	// Основной эффект для запуска запросов
	// ONLY triggers when filterQuery changes (when user applies filter or URL changes)
	// Does NOT trigger when user just modifies filter state in modal
	useEffect(() => {
		fetchProfiles(showMore);

		// Cleanup
		return () => {
			// console.log('=> CLEANUP running (effect re-run detected)');
			if (timeoutIdRef.current) {
				clearTimeout(timeoutIdRef.current);
				timeoutIdRef.current = null;
			}
			if (controllerRef.current) {
				controllerRef.current.abort();
				controllerRef.current = null;
			}
		};
	}, [
		// Only primitive dependencies that affect API calls
		sort_param,
		lang,
		page,
		citySlug,
		districtId,
		agencyId,
		searchValue,
		filter_query_string,
		isMainPage,
		// filterQuery string, not filterState
		// Do NOT include params or location.pathname here
		// Those are tracked through filterQuery already
	]);

	return {
		showSkeletons,
		isDataEmpty,
		setIsDataEmpty,
		showMore,
		setShowMore,
		parseDisabledFilters,
		requestIdRef,
		fetchProfilesCount,
	};
};
